// 확장자를 적는 이유: 이 파일은 단위 테스트가 앱 번들 밖(node)에서 그대로 읽는다 — node ESM 은 상대 경로의
// 확장자를 추론하지 않는다. webpack 은 확장자가 있어도 같은 파일을 찾는다.
import {
  REQUEST_ROUTE_LAYOUT,
  REQUEST_SENT_AT,
  csrfHeaders,
  nextAttemptConfig,
  retryDelay,
  showsErrorToast,
  showsLoading,
  stampedRouteLayout,
} from './policy.js'

/**
 * **공통 HTTP 인터셉터 — 요청마다 도는 순서 한 곳.** 무엇을 할지는 `policy.js` 의 순수 판정이 정하고, 효과(스토어·토스트·
 * 로그아웃·대기)는 `index.js`(경계)가 주입한다. 이 파일이 스토어·Element·Vue 를 import 하지 않는 이유는 하나다: 재시도처럼
 * **순서가 곧 계약**인 것(로딩 카운트가 맞는가, 토스트가 몇 번인가, 재요청이 어떤 헤더·스냅샷을 싣는가)을 node 러너가 실제
 * axios 인스턴스 위에서 볼 수 있어야 한다(`tests/unit/httpRetry.test.js`).
 *
 * 한 시도의 순서:
 *
 * 1. 요청 — CSRF 헤더(지금 토큰) · 라우트 스냅샷(첫 시도만 지금 라우트, 12R P10 — [stampedRouteLayout]) · 이 시도를 보낸 시각
 *    (재시도 판정이 「빨리 끝난 연결 실패인가」를 본다 — 리뷰 M2) · 로딩 올림.
 * 2. 성공 — 로딩 내림 → 응답 해석(`handleResponse`).
 * 3. 실패 — [retryDelay] 가 대기를 주면 기다린 뒤 같은 요청의 다음 시도를 보내고, **이 시도의 로딩은 그 재요청이 끝날 때**
 *    내린다. 먼저 내리면 다음 시도가 올리기 전(요청 인터셉터는 다음 마이크로태스크에 돈다)에 Vue 가 카운트 0 을 보고 전역
 *    마스크를 닫았다 다시 연다(리뷰 L1) — 논리적으로 한 요청이 아직 날아가는 중이다. 카운트 이력은 `1,2,3,2,1,0` 이다.
 *    재시도 중에는 아무 말도 하지 않는다. 다시 보내지 않으면 로딩을 내리고, 응답이 있으면 응답 해석(토스트·403·`HttpError`),
 *    없으면 전송 오류 토스트(옵트아웃 존중, 14R P58) — **마지막 실패에서 한 번만** — 뒤 원래 오류를 던진다.
 *
 * @param {import('axios').AxiosInstance} client
 * @param {{
 *   csrfToken: function(): ?string,
 *   route: function(): ?object,
 *   now: function(): number,
 *   setLoading: function(boolean): void,
 *   handleResponse: function(object): *,
 *   showTransportError: function(): void,
 *   wait: function(number): Promise<void>,
 * }} effects
 *   - `route` — `router.afterEach` 가 커밋한 **확정된** 라우트(라우터를 직접 import 하면 순환)
 *   - `now` — 시계(ms). 시도를 보낸 시각과 실패한 시각을 이것으로 잰다
 *   - `handleResponse` — 응답 해석. 4xx·5xx 면 토스트(옵트아웃 존중)·403 처리 뒤 `HttpError` 를 던진다
 * @returns {import('axios').AxiosInstance} 같은 인스턴스
 */
export function installInterceptors(client, effects) {
  const { csrfToken, route, now, setLoading, handleResponse, showTransportError, wait } = effects

  client.interceptors.request.use(
    (config) => {
      // 서버가 받는 CSRF 헤더 — 이름·모양은 `policy.csrfHeaders` 한 곳이고, 값은 시도마다 지금 토큰이다(토큰이 없으면 싣지 않는다)
      Object.assign(config.headers, csrfHeaders(csrfToken()))

      // 403 판정은 응답 시점의 전역 라우트가 아니라 이 스냅샷을 읽는다(12R P10)
      config[REQUEST_ROUTE_LAYOUT] = stampedRouteLayout(config, route())
      // 시도마다 새로 찍는다 — 재시도 판정은 「이 시도가 보낸 뒤 얼마 만에 실패했나」를 본다
      config[REQUEST_SENT_AT] = now()

      if (showsLoading(config)) {
        setLoading(true)
      }

      return config
    }
    // 거부 갈래는 두지 않는다 — 이 인터셉터가 체인의 첫 것이라 닿을 일이 없고, 닿더라도 이 시도는 로딩을 올린 적이 없다
    // (종전의 무조건 내림은 올리지 않은 카운트를 깎는 자리였다)
  )

  client.interceptors.response.use(
    async (response) => {
      if (showsLoading(response.config)) {
        setLoading(false)
      }

      await handleResponse(response)

      return response
    },
    async (error) => {
      const config = error && error.config
      const delay = retryDelay(error, now())
      const releaseLoading = () => {
        if (showsLoading(config)) {
          setLoading(false)
        }
      }

      if (delay !== null) {
        await wait(delay)

        // 이 시도의 로딩은 재요청이 끝날 때 내린다 — 사이에 카운트가 0 을 찍지 않게(리뷰 L1)
        return client.request(nextAttemptConfig(config)).finally(releaseLoading)
      }

      releaseLoading()

      if (error && error.response) {
        await handleResponse(error.response)
      } else if (showsErrorToast(config)) {
        // 전송 오류(무응답·타임아웃)도 옵트아웃을 지킨다 (14R P58) — 폴링 화면이 오프라인마다 빨간 토스트를 띄우지 않게.
        // 대체 동작(인라인 안내)은 옵트아웃한 호출부의 몫이다. 어느 갈래든 오류는 그대로 던져진다
        showTransportError()
      }

      return Promise.reject(error)
    }
  )

  return client
}
